import { Html5Qrcode } from 'html5-qrcode';
import React, { useEffect, useRef, useState } from 'react';

interface CameraDevice {
    id: string;
    label: string;
}

interface QrScannerProps {
    /**
     * Function to call when a QR code is successfully scanned
     */
    onScanSuccess: (decodedText: string) => void;

    /**
     * Function to call when an error occurs during scanning
     */
    onScanError?: (error: string) => void;

    /**
     * Width of the scanner viewport (can be percentage string like '100%')
     */
    width?: number | string;

    /**
     * Height of the scanner viewport (can be percentage string like '100%')
     */
    height?: number | string;

    /**
     * FPS for scanning
     */
    fps?: number;

    /**
     * Whether to show a border around the scanning area
     */
    showBorder?: boolean;

    /**
     * CSS class name for the container
     */
    className?: string;

    /**
     * Whether to show the scanning indicator
     */
    showScanningIndicator?: boolean;

    /**
     * Whether the scanner is active
     */
    isActive?: boolean;

    /**
     * Whether to show camera selector
     */
    showCameraSelector?: boolean;
}

/**
 * QR Scanner component using html5-qrcode library
 *
 * This component provides a camera-based QR code scanner that
 * can be used to scan QR codes and extract their content.
 */
const QrScanner: React.FC<QrScannerProps> = ({
    onScanSuccess,
    onScanError,
    width = 300,
    height = 300,
    fps = 10,
    showBorder = true,
    className = '',
    showScanningIndicator = true,
    isActive = true,
    showCameraSelector = true,
}) => {
    const [isScanning, setIsScanning] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [cameras, setCameras] = useState<CameraDevice[]>([]);
    const [selectedCamera, setSelectedCamera] = useState<string | null>(null);
    const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
    const scannerRef = useRef<Html5Qrcode | null>(null);
    const scannerContainerId = 'qr-scanner-container';

    // Load available cameras
    const loadCameras = async () => {
        try {
            const devices = await Html5Qrcode.getCameras();
            if (devices && devices.length > 0) {
                setCameras(devices);
                // Default to back camera if available (usually index 1)
                const defaultCamera = devices.length > 1 ? devices[1].id : devices[0].id;
                setSelectedCamera(defaultCamera);
            }
        } catch (err) {
            console.error('Error getting cameras:', err);
            const errorMsg = err instanceof Error ? err.message : 'Failed to get cameras';
            setError(errorMsg);
            if (onScanError) onScanError(errorMsg);
        }
    };

    // Initialize the scanner when the component mounts
    useEffect(() => {
        // Create scanner instance
        scannerRef.current = new Html5Qrcode(scannerContainerId);

        // Load available cameras
        loadCameras();

        // Clean up when component unmounts
        return () => {
            stopScanner();
        };
    }, []);

    // Handle changes to isActive prop or selectedCamera
    useEffect(() => {
        if (isActive && selectedCamera && !isScanning && scannerRef.current && !isTransitioning) {
            startScanner();
        } else if (!isActive && isScanning && scannerRef.current && !isTransitioning) {
            stopScanner();
        }
    }, [isActive, selectedCamera]);

    // Handle camera change
    const handleCameraChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newCameraId = event.target.value;

        // If already scanning, stop scanner first
        if (isScanning) {
            await stopScanner();
        }

        setSelectedCamera(newCameraId);
    };

    /**
     * Start the QR code scanner
     */
    const startScanner = async () => {
        if (!scannerRef.current || !selectedCamera || isScanning || isTransitioning) return;

        try {
            // Set transitioning state to prevent multiple concurrent calls
            setIsTransitioning(true);
            setIsScanning(true);
            setError(null);

            // Calculate qrbox size based on container dimensions
            let qrboxWidth = 250;
            let qrboxHeight = 250;

            if (typeof width === 'number' && typeof height === 'number') {
                qrboxWidth = Math.min(width - 50, 250);
                qrboxHeight = Math.min(height - 50, 250);
            }

            // Start scanning with selected camera
            await scannerRef.current.start(
                selectedCamera,
                {
                    fps,
                    qrbox: { width: qrboxWidth, height: qrboxHeight },
                },
                (decodedText) => {
                    // Call the success callback with the decoded text
                    onScanSuccess(decodedText);
                },
                (errorMessage) => {
                    // Only handle fatal errors, ignore transient scanning errors
                    if (errorMessage.includes('Camera access denied') || errorMessage.includes('No camera')) {
                        setError(errorMessage);
                        if (onScanError) onScanError(errorMessage);
                    }
                },
            );
        } catch (err) {
            const errorMsg = err instanceof Error ? err.message : 'Failed to start scanner';
            setError(errorMsg);
            if (onScanError) onScanError(errorMsg);
            setIsScanning(false);
        } finally {
            // Always reset the transitioning state
            setIsTransitioning(false);
        }
    };

    /**
     * Stop the QR code scanner
     */
    const stopScanner = async () => {
        if (!scannerRef.current || !isScanning || isTransitioning) return;

        try {
            setIsTransitioning(true);
            await scannerRef.current.stop();
            console.log('QR scanner stopped');
        } catch (err) {
            console.error('Error stopping scanner:', err);
        } finally {
            setIsScanning(false);
            setIsTransitioning(false);
        }
    };

    // Convert width and height to style values
    const widthStyle = typeof width === 'number' ? `${width}px` : width;
    const heightStyle = typeof height === 'number' ? `${height}px` : height;

    return (
        <div className={`qr-scanner ${className}`}>
            <div className="scanner-card rounded-lg bg-white p-4 shadow-lg">
                {/* Header with title and camera selector */}
                <div className="scanner-header mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-800">Scan QR Code</h3>

                    {/* Camera selector with icon */}
                    {showCameraSelector && cameras.length > 1 && (
                        <div className="camera-selector flex items-center">
                            <svg
                                className="mr-2 h-5 w-5 text-gray-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                                />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <select
                                id="camera-select"
                                className="rounded-md border border-gray-300 bg-gray-50 px-3 py-1 text-sm text-gray-700 focus:border-blue-500 focus:ring-blue-500 focus:outline-none"
                                value={selectedCamera || ''}
                                onChange={handleCameraChange}
                            >
                                {cameras.map((camera) => (
                                    <option key={camera.id} value={camera.id}>
                                        {camera.label || `Camera ${cameras.indexOf(camera) + 1}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                {/* Scanner viewport with visual guides */}
                <div className="scanner-viewport-container relative">
                    {/* Actual scanner container */}
                    <div
                        id={scannerContainerId}
                        className="overflow-hidden rounded-lg"
                        style={{
                            width: widthStyle,
                            height: heightStyle,
                            position: 'relative',
                        }}
                    ></div>

                    {/* Overlay with scanning guides */}
                    {isScanning && !error && showScanningIndicator && (
                        <div className="pointer-events-none absolute inset-0">
                            {/* Corner indicators */}
                            <div className="absolute top-0 left-0 h-16 w-16 border-t-2 border-l-2 border-blue-500"></div>
                            <div className="absolute top-0 right-0 h-16 w-16 border-t-2 border-r-2 border-blue-500"></div>
                            <div className="absolute bottom-0 left-0 h-16 w-16 border-b-2 border-l-2 border-blue-500"></div>
                            <div className="absolute right-0 bottom-0 h-16 w-16 border-r-2 border-b-2 border-blue-500"></div>

                            {/* Scanning animation */}
                            <div className="animate-scanline absolute top-1/2 left-0 h-0.5 w-full bg-blue-500 opacity-70"></div>
                        </div>
                    )}
                </div>

                {/* Status indicator */}
                <div className="scanner-status mt-4">
                    {isScanning && !error && showScanningIndicator && (
                        <div className="flex items-center justify-center">
                            <div className="mr-2 h-3 w-3 animate-pulse rounded-full bg-green-500"></div>
                            <span className="text-sm text-gray-600">Position QR code within the frame</span>
                        </div>
                    )}

                    {/* Error message */}
                    {error && (
                        <div className="rounded-md border-l-4 border-red-500 bg-red-50 p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                        />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-700">{error}</p>
                                    <button
                                        className="mt-2 text-sm font-medium text-red-700 hover:text-red-600 focus:outline-none"
                                        onClick={startScanner}
                                    >
                                        Try again
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {!isScanning && !error && <div className="text-center text-sm text-gray-500">Scanner is inactive</div>}
                </div>

                {/* Instructions */}
                <div className="scanner-instructions mt-4 border-t pt-3 text-center text-sm text-gray-500">
                    Point your camera at a QR code to scan automatically
                </div>
            </div>

            {/* Add custom styles for animation */}
            <style>{`
                @keyframes scanline {
                    0% {
                        transform: translateY(-100px);
                        opacity: 0;
                    }
                    50% {
                        opacity: 1;
                    }
                    100% {
                        transform: translateY(100px);
                        opacity: 0;
                    }
                }
                .animate-scanline {
                    animation: scanline 2s linear infinite;
                }
            `}</style>
        </div>
    );
};

export default QrScanner;
